import { BookData, ChapterData } from 'src/interfaces'
import { downloadFileUrl, getFilenameFromUrl } from 'src/utils/fileUrl'

// UD-614: the English Word file of a chapter, the way the Book UniVini page shows it.

export interface ChapterWordFile {
	// The original file as it was uploaded. Empty when the chapter has none.
	url: string
	// The name the file had on the uploader's computer when the server kept it, otherwise a
	// readable name made from the book and the chapter number.
	name: string
	// False for a file uploaded before names were kept.
	nameRecorded: boolean
}

type ContentEntry = {
	docUrl?: string
	docFileName?: string
	language?: string
}

const safeNamePart = (value: string) =>
	value
		.replace(/[\\/:*?"<>|]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()

export const getChapterWordFile = (
	chapter?: ChapterData | null,
	book?: BookData | null,
): ChapterWordFile => {
	const entries = (
		Array.isArray(chapter?.content) ? chapter?.content : []
	) as ContentEntry[]
	const english =
		entries.find((item) => item?.language === 'en') ??
		// Chapters made before the 17-language flow keep the English file under "en-en"
		entries.find((item) => item?.language === 'en-en')

	// The chapter's own field is the file in use. The copy inside `content` is read only when
	// that field is empty (older chapters).
	const url = chapter?.original_doc_url || english?.docUrl || ''
	if (!url) return { url: '', name: '', nameRecorded: false }

	const recorded = entries.find(
		(item) => item?.docUrl === url && item?.docFileName,
	)?.docFileName
	if (recorded) return { url, name: recorded, nameRecorded: true }

	const title = safeNamePart(book?.title || chapter?.title || '')
	const number = chapter?.chapter_number
	const extension = /\.doc$/i.test(getFilenameFromUrl(url)) ? '.doc' : '.docx'
	const base = [title, number ? `Chapter ${number}` : '']
		.filter(Boolean)
		.join(' - ')

	return {
		url,
		name: base ? `${base}${extension}` : getFilenameFromUrl(url),
		nameRecorded: false,
	}
}

/**
 * Saves the original file under the given name. The file lives on another address than the
 * CMS, where a browser ignores the name on a plain link, so the file is read first and saved
 * from memory. If it cannot be read that way the plain link is opened (the browser then picks
 * the name).
 */
export const downloadWordFile = async (url: string, name: string) => {
	try {
		const response = await fetch(url)
		if (!response.ok) throw new Error(`HTTP ${response.status}`)
		const blob = await response.blob()
		const objectUrl = URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = objectUrl
		link.download = name
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
	} catch {
		downloadFileUrl(url)
	}
}

// The CMS replaces a Word file in one way only (the owner's order, UD-614): the file is
// replaced and the words it changes go into the English text; everything else is kept. These
// are the two short lines shown for it.
export const WORD_FILE_REPLACE_NOTICE =
	'Replaces the English Word file for small corrections. Audio and translations stay as they are.'
export const WORD_FILE_FULL_CHANGE_ADVICE =
	'To change the whole content: delete the book and upload it again.'

// What the server answers when it refuses to replace a file as a text fix.
export const CHAPTER_TEXT_FIX_REFUSED = 'chapter_text_fix_refused'

export interface TextFixRefusal {
	reason?: string
	old_pages?: number
	new_pages?: number
	page?: number
	old_paragraphs?: number
	new_paragraphs?: number
	corrections_not_placed?: number
	message?: string
}

export const getTextFixRefusal = (error: any): TextFixRefusal | null => {
	const body = error?.response?.data
	if (body?.type !== CHAPTER_TEXT_FIX_REFUSED) return null
	return { ...(body?.data || {}), message: body?.message }
}

const count = (value: number | undefined, one: string, many: string) =>
	`${value ?? '?'} ${value === 1 ? one : many}`

// One short sentence saying why the file was not replaced
export const describeTextFixRefusal = (refusal: TextFixRefusal): string => {
	switch (refusal.reason) {
		case 'page_count_changed':
			return `The new file has ${count(refusal.new_pages, 'page', 'pages')}, the current one has ${refusal.old_pages ?? '?'}.`
		case 'paragraph_count_changed':
			return `Page ${refusal.page ?? '?'} has ${count(refusal.new_paragraphs, 'paragraph', 'paragraphs')} in the new file, the current one has ${refusal.old_paragraphs ?? '?'}.`
		case 'corrections_not_placed':
			return 'The changes in the new file could not be matched to the stored text.'
		case 'audio_in_progress':
			return 'Audio or translations of this chapter are still being made. Try again later.'
		case 'no_previous_text':
			return 'This chapter has no stored English text.'
		default:
			return refusal.message || 'The new file could not be used.'
	}
}

// What a replace did, as the server reports it
export interface TextFixResult {
	corrections_applied?: number
	corrections_not_placed?: number
	english_rows_left?: number
}

export const getTextFixResult = (response: any): TextFixResult =>
	response?.data?.results?.object?.text_fix || {}

// True when something the new file changes is NOT in the English text the app shows
export const isTextFixPartial = (result: TextFixResult) =>
	(result.corrections_not_placed ?? 0) > 0 || (result.english_rows_left ?? 0) > 0

/**
 * What to tell staff after a replace. It never says only "replaced" when a correction was left
 * out: the number left out is part of the sentence.
 */
export const describeTextFixResult = (result: TextFixResult): string => {
	const applied = result.corrections_applied
	const left = result.corrections_not_placed ?? 0
	const parts = ['Word file replaced.']
	if (typeof applied === 'number') {
		parts.push(
			applied === 0 && left === 0
				? 'The text was already the same.'
				: `${count(applied, 'correction', 'corrections')} applied.`,
		)
	}
	if (left > 0) {
		parts.push(
			`${count(left, 'correction', 'corrections')} could not be placed and ${left === 1 ? 'is' : 'are'} NOT in the English text.`,
		)
	}
	if ((result.english_rows_left ?? 0) > 0) {
		parts.push('The English text shown in the app was not updated.')
	}
	return parts.join(' ')
}
